import { useMemo } from 'react';

interface DynamicBannerProps {
  children: React.ReactNode;
  className?: string;
  /** Enable streaming code effect on sides (default: false) */
  showStreamingCode?: boolean;
}

// Code snippets that stream down the banner
const CODE_ITEMS = [
  'მπ({B},B_i)/მB_i',
  '∑C_kj(B_kj)',
  'λ_c>0',
  'MUx=Px',
  'MUx>Px',
  'MUx<Px',
  'mRP = ∆TR/∆Q',
  'rank{P}',
  'argmax Γ(X) ',
  'python opt_budget(',
  'torch.tensor(bid_features)',
  'class SimpleNet(nn.Module):',
  'torch.cuda.manual_seed(seed)',
];

// Generate random binary/symbol strings
const generateRandomCode = () => {
  const chars = ['0', '1', '{', '}', '<', '>', '/', '*', '+', '='];
  return Array.from({ length: 8 })
    .map(() => chars[Math.floor(Math.random() * chars.length)])
    .join('');
};

/**
 * DynamicBanner - An animated banner with streaming code effect
 *
 * Features a dark gray/pink gradient background with Matrix-style
 * streaming code characters on the sides.
 */
export function DynamicBanner({ children, className = '', showStreamingCode = false }: DynamicBannerProps) {
  // Generate stream columns with stable random values
  const streamColumns = useMemo(() => {
    return Array.from({ length: 20 }).map((_, i) => ({
      left: `${i * 5 + Math.random() * 2}%`,
      animationDelay: `${Math.random() * 5}s`,
      animationDuration: `${5 + Math.random() * 3}s`,
      content: Math.random() > 0.3
        ? CODE_ITEMS[Math.floor(Math.random() * CODE_ITEMS.length)]
        : generateRandomCode(),
    }));
  }, []);

  return (
    <>
      <style>{`
        @keyframes bannerGradientShift {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        @keyframes bannerCodeStream {
          0% {
            transform: translateY(-100%);
            opacity: 0;
          }
          5% {
            opacity: 0.6;
          }
          95% {
            opacity: 0.6;
          }
          100% {
            transform: translateY(400%);
            opacity: 0;
          }
        }
        .banner-code-stream {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          pointer-events: none;
          border-radius: 0.5rem;
          mask-image: linear-gradient(90deg,
            rgba(0,0,0,0.7) 0%,
            rgba(0,0,0,0.7) 20%,
            transparent 35%,
            transparent 65%,
            rgba(0,0,0,0.7) 80%,
            rgba(0,0,0,0.7) 100%
          );
          -webkit-mask-image: linear-gradient(90deg,
            rgba(0,0,0,0.7) 0%,
            rgba(0,0,0,0.7) 20%,
            transparent 35%,
            transparent 65%,
            rgba(0,0,0,0.7) 80%,
            rgba(0,0,0,0.7) 100%
          );
        }
        .banner-code-column {
          position: absolute;
          top: 0;
          font-family: monospace;
          font-size: 12px;
          line-height: 1.2;
          color: rgba(255, 255, 255, 0.7);
          white-space: nowrap;
          animation: bannerCodeStream 3s linear infinite;
        }
      `}</style>
      <div
        className={`relative text-white rounded-lg p-4 text-center font-semibold overflow-hidden ${className}`}
        style={{
          background: 'linear-gradient(90deg, #373636 0%, #373636 25%, #ee3962 50%, #ee3962 75%, #373636 100%)',
          backgroundSize: '200% 100%',
          animation: 'bannerGradientShift 12s ease-in-out infinite',
        }}
      >
        {/* Code streaming effect overlay */}
        {showStreamingCode && (
          <div className="banner-code-stream">
            {streamColumns.map((col, i) => (
              <span
                key={i}
                className="banner-code-column"
                style={{
                  left: col.left,
                  animationDelay: col.animationDelay,
                  animationDuration: col.animationDuration,
                }}
              >
                {col.content}
              </span>
            ))}
          </div>
        )}
        <span className="relative z-10">{children}</span>
      </div>
    </>
  );
}
